import React, { useEffect, useState, useRef  } from 'react';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactModal from 'react-modal';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footerbar from './components/Footerbar';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config';

import imageCompression from 'browser-image-compression';
import { Link, useLocation } from 'react-router-dom';
import ImageWithLoader2 from './components/ImageWithLoader2';

function GalleryImages() {
  const { navigate, isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();

  const location = useLocation();
    const albumId = location.state?.albumId;

  const queryClient = useQueryClient();
  const loggedInUserId = localStorage.getItem("user_id");
  const loggedFamilyAdmin = localStorage.getItem("family_admin");

  const [files, setFiles] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const [isUploading, setIsUploading] = useState(false);
  

  const { data: gallery, isLoading, isError, refetch } = useQuery({
    queryKey: ['familyGallery', albumId], // include albumId in cache key
    queryFn: () =>
      axios.get(`${API_URL}/family-gallery-api`, {
        withCredentials: true,
        params: { albumId }, //  pass albumId as query param
      }).then((response) => {
        if (response.data.Status === "Success") {
          return response.data.images;
        } else {
          throw new Error('Failed to fetch gallery images');
        }
      }),
    enabled: !!albumId, // don't run until albumId is available
    staleTime: 0,
    cacheTime: Infinity,
  });


  useEffect(() => {
    const handleKey = (e) => {
      if (selectedIndex === null || !gallery || gallery.length === 0) return;
      if (e.key === "ArrowRight") {
        setSelectedIndex((prev) => (prev + 1) % gallery.length);
      }
      if (e.key === "ArrowLeft") {
        setSelectedIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
      }
      if (e.key === "Escape") {
        setSelectedIndex(null);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedIndex, gallery]);


//add image to album


const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;
  
    try {
      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 800,
        useWebWorker: true,
      };
  
      const compressedFiles = await Promise.all(
        selectedFiles.map(async (file) => {
          try {
            const compressed = await imageCompression(file, options);
            return compressed;
          } catch (err) {
            console.error(`Compression failed for ${file.name}:`, err);
            toast?.error?.(`Failed to compress ${file.name}`);
            return null;
          }
        })
      );
  
      const validCompressedFiles = compressedFiles.filter(Boolean);
  
      setFiles(validCompressedFiles); // <-- your state to hold compressed images
  
      console.log("Compressed files:", validCompressedFiles);
    } catch (err) {
      console.error("Image compression error:", err);
      toast?.error?.("Failed to compress images.");
    }
  };

 
  const handleImageUpload = async () => {
    

      if (!albumId) {
        toast.error("Please select a album first", { position: "top-right", autoClose: 3000 });
        return;
      }
    
      if (files.length === 0) {
        toast.error("Please select at least one image", { position: "top-right", autoClose: 3000 });
        return;
      }
  
    setIsUploading(true); // Disable button
  
    try {
      const targetKB = 100;
      const compressedFinal = [];
  
      for (const file of files) {
        let quality = 0.9;
        let compressedFile = file;
  
        while (true) {
          const options = {
            maxSizeMB: targetKB / 1024,
            initialQuality: quality,
            useWebWorker: true,
          };
  
          compressedFile = await imageCompression(file, options);
  
          if (compressedFile.size / 1024 <= targetKB || quality <= 0.1) break;
          quality -= 0.1;
        }
  
        compressedFinal.push(compressedFile);
      }
  
      const formData = new FormData();
      compressedFinal.forEach((file) => formData.append("images", file));
  
      await axios.post(`${API_URL}/upload-gallery-api/${albumId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
  
      toast.success("Images uploaded successfully", { position: "top-right", autoClose: 3000 });
      setUploadModalOpen(false);
      
      setFiles([]);
      refetch();
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Upload failed", { position: "top-right", autoClose: 3000 });
    } finally {
      setIsUploading(false); // Re-enable button
    }
  };




  //edit image
const [editImageModalOpen, setEditImageModalOpen] = useState(false);
const [selectedImage, setSelectedImage] = useState(null);  // The image being edited
const [compressedPreview, setCompressedPreview] = useState(null); // Preview new compressed image

const closeEditModal = () => {
  setEditImageModalOpen(false);
  setCompressedPreview(null); // Clear preview when closing
};

// Handle file selection and compression
// Handle file selection + compression
const handleFileChangeEdit = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  try {
    const options = {
      maxSizeMB: 0.2,             // ~200KB target
      maxWidthOrHeight: 800,      // Resize large images
      useWebWorker: true,
    };

    const compressedFile = await imageCompression(file, options);

    // Replace preview URL with new compressed one
    const previewUrl = URL.createObjectURL(compressedFile);
    setCompressedPreview(previewUrl);

    // Attach new compressed file to selected image for upload later
    setSelectedImage((prev) => ({
      ...prev,
      newFile: compressedFile,
    }));

  } catch (err) {
    console.error("Compression failed:", err);
    toast.error("Failed to compress image.");
  }
};


const [isUploadingEdit, setIsUploadingEdit] = useState(false);

const handleEditUpload = async () => {
  if (!selectedImage || !selectedImage.newFile) {
    toast.error("Please select an image to update", { position: "top-right", autoClose: 3000 });
    return;
  }

  setIsUploadingEdit(true);

  try {
    const targetKB = 100;
    let quality = 0.9;
    let compressedFile = selectedImage.newFile;

    while (true) {
      const options = {
        maxSizeMB: targetKB / 1024,
        initialQuality: quality,
        useWebWorker: true,
      };

      compressedFile = await imageCompression(selectedImage.newFile, options);

      if (compressedFile.size / 1024 <= targetKB || quality <= 0.1) break;
      quality -= 0.1;
    }

    const formData = new FormData();
    formData.append("image", compressedFile);
    formData.append("id", selectedImage.id);          // Image ID in DB
    formData.append("oldImage", selectedImage.image_name); // Old image for deletion

    await axios.post(`${API_URL}/edit-gallery-api`, formData, {
      withCredentials: true,
      headers: { "Content-Type": "multipart/form-data" },
    });

    toast.success("Image updated successfully", { position: "top-right", autoClose: 3000 });
    setEditImageModalOpen(false);
    setCompressedPreview(null);
    setSelectedImage(null);
    refetch();
  } catch (err) {
    toast.error("Update failed", { position: "top-right", autoClose: 3000 });
    console.error("Update error:", err);
  } finally {
    setIsUploadingEdit(false);
  }
};


const handleDeleteUploaded = async () => {
  if (!selectedImage) {
    toast.error("No image selected", { position: "top-right", autoClose: 3000 });
    return;
  }

  setIsUploadingEdit(true);

  try {
    await axios.post(`${API_URL}/delete-gallery-api`, {
      id: selectedImage.id,
      image_name: selectedImage.image_name,
    }, {
      withCredentials: true,
    });

    toast.success("Image deleted successfully", { position: "top-right", autoClose: 3000 });

    // Close modal and reset state
    setEditImageModalOpen(false);
    setCompressedPreview(null);
    setSelectedImage(null);

    // Also close the fullscreen viewer (so no blank screen)
    setSelectedIndex(null);

    // Refresh gallery list
    refetch();

  } catch (err) {
    toast.error("Failed to delete image", { position: "top-right", autoClose: 3000 });
    console.error("Delete error:", err);
  } finally {
    setIsUploadingEdit(false);
  }
};


     //image zooming

     const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState(null);
  const [dragging, setDragging] = useState(false);
  const lastPos = useRef({ x: 0, y: 0 });


  const rafRef = useRef(null);

const updateTransform = (newScale, newOffset) => {
  if (rafRef.current) cancelAnimationFrame(rafRef.current);
  rafRef.current = requestAnimationFrame(() => {
    setScale(newScale);
    setOffset(newOffset);
  });
};


  return (
    <div className="scrollPageContainer theme-light">
      <div className="main-wrapper">
        {isSidebarVisible && <Sidebar />}
        <Header toggleSidebar={toggleSidebar} />
        <Sidebar show={showSidebar} toggle={toggleSidebar} />
        <Footerbar toggleSidebar={toggleSidebar} />
        <ToastContainer />

        <div className="page-wrapper">
          <div className="content container-fluid">

          {/*<div
  onClick={() => navigate(-1)}
  style={{ textDecoration: 'none', color: '#184A2C', cursor: 'pointer' }}
>
  <i className='fa fa-arrow-left' style={{ fontSize: '18px' }}></i>
</div>*/}

<div className="d-flex align-items-center justify-content-between mb-3 py-2 border-bottom">
 
  <button
    className="btn btn-link d-inline-flex align-items-center text-success fw-semibold p-0"
    onClick={() => navigate(-1)}
  >
    <i className="bi bi-arrow-left me-1" /> 
  </button>

  <div className="d-flex align-items-center gap-2">



    <button
      className="btn btn-primary btn-sm d-flex align-items-center shadow-sm"
      onClick={() => {
        setUploadModalOpen(true);
        }}
    >
      <i className="bi bi-plus-lg me-1" />
      
    </button>
    

   
  </div>
</div>





<ReactModal
              isOpen={uploadModalOpen}
              onRequestClose={() => setUploadModalOpen(false)}
              className="Modal Modal--lg"
              overlayClassName="Overlay Overlay--blurx"
            >
              <div className="Modal__header">
                <div className="Modal__icon"><i className="bi bi-cloud-arrow-up" /></div>
                <div>
                  <h6 className="Modal__title mb-0">Upload images</h6>
                  
                </div>
                <button type="button" className="btn-close ms-auto" onClick={() => setUploadModalOpen(false)} />
              </div>

              <div className="Modal__body">
                <div
                 
                >
                  <label htmlFor="imageUploadEdit" className="btn btn-outline-success d-inline-flex align-items-center gap-2 w-100">
                    <i className="bi bi-upload" /> Select images
                  </label>
                  <input id="imageUploadEdit" type="file" multiple accept="image/*" onChange={handleFileChange} className="d-none" />
                </div>

                {files.length > 0 && (
                  <div className="mt-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <strong className="small mb-0">{files.length} selected</strong>
                      <button className="btn btn-sm btn-link text-danger p-0" onClick={() => setFiles([])}>Clear all</button>
                    </div>
                    <div className="row g-2">
                      {files.map((file, idx) => (
                        <div className="col-4 col-md-3 col-lg-2" key={idx}>
                          <div className="ratio ratio-1x1 rounded overflow-hidden border">
                            <img src={URL.createObjectURL(file)} alt={`preview-${idx}`} className="w-100 h-100 object-fit-cover" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="Modal__footer">
                
                <button className="btn btn-primary" onClick={handleImageUpload} disabled={isUploading}>
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" /> Uploading…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-cloud-arrow-up me-2" /> Upload
                    </>
                  )}
                </button>
              </div>
            </ReactModal>




            <div className="content my-4">
              {isLoading ? (
                <div className="loadingspinnerDiv">
                <div className="loadingspinner"></div>
              </div>
              ) : isError ? (
                <p>Error loading family gallery.</p>
              ) : (
                <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 g-3">
                  {gallery && gallery.length > 0 ? (
                    gallery.map((image, index) => (
                      <div key={image.id} className="col">
                        <div
                          onClick={() => setSelectedIndex(index)}
                          style={{
                            width: '100%',
                            aspectRatio: 1,
                            overflow: 'hidden',
                            borderRadius: '0px',
                            cursor: 'pointer',
                          }}
                        >
                          <ImageWithLoader2
                            src={`${API_URL}/images/${image.image_name}`}
                            placeholder="https://oret.africa/tree/img/imageplaceholder-icon.jpg"
                        fadeDuration={800}
                          blurAmount={15} 
                            alt={image.description}
                            loading="lazy"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          
                        </div>
                        
                      </div>
                    ))
                  ) : (
                    <p>No gallery images found.</p>
                  )}
                </div>
              )}
            </div>

            {/* Lightbox Modal */}
            {selectedIndex !== null && gallery && (
                  
                  
                 
                      
                  <div
                  className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                  style={{ backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 1050, touchAction: 'none' }}
                  onClick={() => setSelectedIndex(null)}
                  onTouchMove={(e) => {
                    e.preventDefault();
                    if (e.touches.length === 2) {
                      const dist = Math.hypot(
                        e.touches[0].clientX - e.touches[1].clientX,
                        e.touches[0].clientY - e.touches[1].clientY
                      );
                      if (lastTouchDistance) {
                        const delta = dist - lastTouchDistance;
                        updateTransform(
                          Math.min(Math.max(scale + delta / 200, 1), 4),
                          offset
                        );
                      }
                      setLastTouchDistance(dist);
                    } else if (e.touches.length === 1 && dragging) {
                      const touch = e.touches[0];
                      updateTransform(scale, {
                        x: offset.x + (touch.clientX - lastPos.current.x),
                        y: offset.y + (touch.clientY - lastPos.current.y),
                      });
                      lastPos.current = { x: touch.clientX, y: touch.clientY };
                    }
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    if (e.touches.length === 2) setLastTouchDistance(null);
                    else {
                      setDragging(true);
                      lastPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
                    }
                  }}
                  onTouchEnd={() => {
                    setDragging(false);
                    setLastTouchDistance(null);
                  }}
                >
                  <div
                    className="position-relative"
                    style={{
                      width: '90vw',
                      height: '80vh',
                      overflow: 'hidden',
                      cursor: dragging ? 'grabbing' : 'grab',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src={`${API_URL}/images/${gallery[selectedIndex].image_name}`}
                      alt={gallery[selectedIndex].description}
                      style={{
                        width: '100%',
                        height: '92%',
                        objectFit: 'contain',
                        transform: `scale(${scale}) translate(${offset.x / scale}px, ${offset.y / scale}px)`,
                        transformOrigin: 'center center',
                        transition: dragging ? 'none' : 'transform 0.2s ease',
                      }}
                      draggable={false}
                    />

                <div className="position-absolute bottom-0 start-0 end-0 p-3 text-center text-white">
                    {gallery[selectedIndex].description}
                  </div>


                  <div
                  className="position-absolute top-0 start-0 w-100 d-flex justify-content-between align-items-center px-3 py-2"
                  style={{ background: 'linear-gradient(180deg, rgba(0,0,0,.55), rgba(0,0,0,0))' }}
                >
                  <div className="text-white small fw-semibold">
                    {selectedIndex + 1} / {gallery.length}
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    {parseInt(loggedInUserId) === parseInt(gallery[selectedIndex].user_id) && (
                      <button
                        className="btn btn-light btn-sm"
                        title="Edit"
                        onClick={() => {
                          setSelectedImage(gallery[selectedIndex]);
                          setEditImageModalOpen(true);
                        }}
                      >
                        <i className="fa fa-edit me-1" /> Edit
                      </button>
                    )}
                    <button
                      className="btn btn-light btn-sm rounded-circle"
                      style={{ width: 36, height: 36 }}
                      onClick={() => setSelectedIndex(null)}
                    >
                      <i className="bi bi-x-lg" />
                    </button>
                  </div>
                </div>

        
                  

        {/* Navigation Buttons */}
        <button
                    className="btn btn-light rounded-circle position-absolute top-50 start-0 translate-middle-y"
                    style={{ width: 44, height: 44 }}
                    onClick={() => setSelectedIndex((selectedIndex - 1 + gallery.length) % gallery.length)}
                  >
                    <i className="fa fa-chevron-left" />
                  </button>
                  <button
                    className="btn btn-light rounded-circle position-absolute top-50 end-0 translate-middle-y"
                    style={{ width: 44, height: 44 }}
                    onClick={() => setSelectedIndex((selectedIndex + 1) % gallery.length)}
                  >
                    <i className="fa fa-chevron-right" />
                  </button>
                  {/*<button
                    className="btn btn-light rounded-circle position-absolute top-0 end-0 m-3"
                    style={{ width: 40, height: 40 }}
                    onClick={() => setSelectedIndex(null)}
                  >
                    ✕
                  </button>*/}

                
<ReactModal
        isOpen={editImageModalOpen}
        onRequestClose={closeEditModal}
        className="Modal"
        overlayClassName="Overlay Overlay--blurx"
                >
                  <div className="Modal__header">
                    <div className="Modal__icon"><i className="bi bi-image" /></div>
                    <div>
                      <h6 className="Modal__title mb-0">Edit Image</h6>
                      <p className="Modal__sub mb-0">Replace or delete this photo.</p>
                    </div>
                    <button type="button" className="btn-close ms-auto" onClick={closeEditModal} />
                  </div>

                  <div className="Modal__body">
                    {compressedPreview ? (
                      <img
                        src={compressedPreview}
                        alt="Compressed Preview"
                        className="w-100 rounded mb-3"
                        style={{ maxHeight: 300, objectFit: 'cover' }}
                      />
                    ) : selectedImage ? (
                      <img
                        src={`${API_URL}/images/${selectedImage.image_name}`}
                        alt={selectedImage.description || 'Photo'}
                        className="w-100 rounded mb-3"
                        style={{ maxHeight: 300, objectFit: 'cover' }}
                      />
                    ) : (
                      <p>No image selected.</p>
                    )}

                    <label htmlFor="imageUploadEdit" className="btn btn-outline-success d-inline-flex align-items-center gap-2">
                      <i className="bi bi-upload" /> Select New Image
                    </label>
                    <input id="imageUploadEdit" type="file" accept="image/*" onChange={handleFileChangeEdit} hidden />
                  </div>

                  <div className="Modal__footer d-flex justify-content-between">
                    <button onClick={handleDeleteUploaded} className="btn btn-outline-danger" disabled={isUploadingEdit}>
                      {isUploadingEdit ? 'Deleting…' : 'Delete'}
                    </button>
                    <button onClick={handleEditUpload} className="btn btn-primary" disabled={isUploadingEdit}>
                      {isUploadingEdit ? 'Updating…' : 'Update'}
                    </button>
                  </div>
                </ReactModal>



                    </div>
                   
                  </div>
                  



                )}


            <div className="row end-page-space"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GalleryImages;
